import { z } from 'zod/v3';
import {
  transformNull,
  transformNullish,
} from '../../../../common/utils/schema';
import { zDataObjectType } from '../../../schema/zDataObjectType';
import { zDataObjectOutputType } from '../../../schema/zDataObjectOutputType';

const AlgorithmSourceDimensionClassifierFormDataSchema = z.object({
  id: z.number(),
  code: z.string(),
  hasCategories: z.boolean(),
  codeFormat: z.string().nullable(),
  dataTypeId: z.number().nullable(),
});

const AlgorithmSourceDimensionCategoryFormDataSchema = z.object({
  id: z.number(),
  code: z.string(),
  categoryType: z.enum([
    // not expecting 'category' here
    'category_group',
    'combined_group',
  ]),
});

export const AlgorithmSourceDimensionFormDataSchema = z.object({
  code: z.string(),
  name: z.string(),
  classifier: transformNull<
    | z.output<typeof AlgorithmSourceDimensionClassifierFormDataSchema>
    | undefined
  >(AlgorithmSourceDimensionClassifierFormDataSchema, undefined).optional(),
  dimensionTypeCode: z.string(),
  allCategories: z.boolean().nullable(),
  category: AlgorithmSourceDimensionCategoryFormDataSchema.nullable(),
  levelNumber: z.number().nullable(),
});

export const AlgorithmSourceIndicatorFormDataSchema = z.object({
  code: z.string(),
  name: z.string(),
  intermediate: z.boolean().nullish(),
});

export const AlgorithmSourceDataObjectDescriptionFormDataSchema = z.object({
  id: z.number(),
  domainId: transformNull<number | undefined>(z.number(), undefined).optional(),
  name: transformNull<string>(z.string(), ''),
  code: transformNull<string>(z.string(), ''),
  dataObjectType: zDataObjectType.nullable(),
  outputTypeCode: zDataObjectOutputType.nullable(),
  dimensions: transformNullish<
    z.output<typeof AlgorithmSourceDimensionFormDataSchema>[],
    z.input<typeof AlgorithmSourceDimensionFormDataSchema>[]
  >(AlgorithmSourceDimensionFormDataSchema.array(), []),
  indicators: transformNullish<
    z.output<typeof AlgorithmSourceIndicatorFormDataSchema>[],
    z.input<typeof AlgorithmSourceIndicatorFormDataSchema>[]
  >(AlgorithmSourceIndicatorFormDataSchema.array(), []),
});

const AlgorithmSourceBaseFormDataSchema = z.object({
  id: transformNull<number>(z.number(), -1),
  numberOfPreviousPeriod: transformNull<number | undefined>(
    z.number(),
    undefined,
  ).optional(),
  alias: transformNull<string>(z.string(), '').optional(),
});

export const AlgorithmSourceFormDataSchema = z.union([
  AlgorithmSourceBaseFormDataSchema.extend({
    dataSource: z.object({
      id: z.number(),
      name: transformNull<string>(z.string(), ''),
      code: transformNull<string>(z.string(), ''),
    }),
  }).transform((data) => ({
    sourceName: data.dataSource.name,
    sourceCode: data.dataSource.code,
    sourceType: 'DB' as const,
    sourceId: `ds-${data.dataSource.id}` as const,
    domainId: null,
    dimensions: [],
    indicators: [],
    ...data,
  })),
  AlgorithmSourceBaseFormDataSchema.extend({
    dataObjectDescription: AlgorithmSourceDataObjectDescriptionFormDataSchema,
  }).transform((data) => ({
    sourceName: data.dataObjectDescription.name,
    sourceCode: data.dataObjectDescription.code,
    sourceType: data.dataObjectDescription.outputTypeCode
      ? data.dataObjectDescription.outputTypeCode
      : data.dataObjectDescription.dataObjectType
        ? data.dataObjectDescription.dataObjectType
        : ('' as const),
    sourceId: `dod-${data.dataObjectDescription.id}` as const,
    domainId: data.dataObjectDescription.domainId,
    dataObjectType: data.dataObjectDescription?.dataObjectType || null,
    outputTypeCode: data.dataObjectDescription?.outputTypeCode || null,
    dataObjectCode: data.dataObjectDescription?.code || null,
    dimensions: data.dataObjectDescription.dimensions,
    indicators: data.dataObjectDescription.indicators,
    ...data,
  })),
]);
